"use client";
import dynamic from "next/dynamic";
import React from "react";
const DynamicFormClient = dynamic(() => import("./dynamic-form-client"), { ssr: false });
import { useMouseMovement } from "@/app/[lang]/MouseMovementContext";
interface Field {
    required: React.JSX.Element;
    isChild: any;
    parentField: any;
    multipleFile: any;
    limitFile: number;
    consentType: string;
    id: string;
    key: string;
    label: string;
    type: string;
    options?: { key: string; label: string }[];
}

const DynamicFormServer = ({
    dynamicForm,
    recaptcha,
    text,
    score,
    googleReCaptchaKey,
    isPageSpeed,
    defaultLanguage,
    radiusStyle,
}: {
    dynamicForm: any;
    recaptcha: boolean;
    text: any;
    score: number;
    googleReCaptchaKey: string | undefined;
    isPageSpeed: boolean;
    defaultLanguage: string;
    radiusStyle: any;
}) => {
    const { hasMouseMoved } = useMouseMovement();
    return hasMouseMoved ? (
        <DynamicFormClient dynamicForm={dynamicForm} recaptcha={recaptcha} text={text} score={score} defaultLanguage={defaultLanguage} radiusStyle={radiusStyle}/>
    ) : (
        <>
            {dynamicForm.fields.map((field: Field) => {
                return (
                    <div className="flex justify-center" key={field.id}>
                        <div className="py-3 w-[600px]">
                            <FieldRenderer field={field} text={text} defaultLanguage={defaultLanguage}/>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

const FieldRenderer = ({ field, text, defaultLanguage }: { field: Field; text: any, defaultLanguage?: string }) => {
    switch (field.type) {
        case "header":
        case "paragraph":
            return (
                <div className={field.type === "header" ? "text-3xl font-semibold text-center" : ""}>
                    <div dangerouslySetInnerHTML={{ __html: field.label }} />
                </div>
            );

        case "privacy":
            return <div className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: field.label }} />;
        case "checkbox":
            const checkboxOptions = Array.isArray(field.options) ? field.options : JSON.parse(field.options || "[]");

            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {checkboxOptions.map((option: any) => (
                        <div key={option.key} className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id={option.key}
                                name={field.key}
                                value={option.key} // กำหนด value เป็น key
                                className="form-check-input"
                            />
                            <label
                                htmlFor={option.key}
                                className={`text-sm
                                `}>
                                {option.label}
                            </label>
                        </div>
                    ))}
                </div>
            );

        case "radio":
            const radioOptions = Array.isArray(field.options) ? field.options : JSON.parse(field.options || "[]");

            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {radioOptions.map((option: any) => (
                        <div key={option.key} className="flex items-center space-x-2">
                            <input
                                type="radio"
                                id={option.key}
                                name={field.key}
                                value={option.key}
                                className="form-check-input"
                            />
                            <label
                                htmlFor={option.key}
                                className={`text-sm
                                `}>
                                {option.label}
                            </label>
                        </div>
                    ))}
                </div>
            );
        case "select":
            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <select
                        id={field.key}
                        name={field.key}
                        className={`form-control w-full p-2 border rounded bg-white`}></select>
                </div>
            );

        case "textarea":
            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                        id={field.key}
                        name={field.key}
                        className={`form-control w-full p-2 border rounded min-h-[10rem]`}
                    />
                </div>
            );

        case "date":
            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="w-full border rounded border-gray-300">
                        <input type="text" placeholder={defaultLanguage === 'th' ? 'วว/ดด/ปปปป' : 'DD/MM/YYYY'} className="w-full p-2 border-none" />
                    </div>
                </div>
            );

        case "upload":
            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <div
                        className="w-full border rounded border-gray-300"
                        style={{
                            border: "2px dashed #ccc",
                            borderRadius: "8px",
                            padding: "16px",
                            textAlign: "center",
                            backgroundColor: "#fafafa",
                            cursor: "pointer",
                        }}>
                        <div className="w-full rounded">
                            <div
                                style={{
                                    margin: 0,
                                    fontWeight: 400,
                                    fontSize: "1rem",
                                    lineHeight: 1.5,
                                    letterSpacing: "0.00938em",
                                    color: "rgba(0, 0, 0, 0.6)",
                                }}>
                                {`${text["drag_drop_files"]} (${text["max_file"]}: ${field.limitFile || "Unlimited"})`}
                            </div>
                        </div>
                    </div>
                </div>
            );

        case "consent":
            const options = Array.isArray(field.options) ? field.options : JSON.parse(field.options || "[]"); // แปลง JSON string เป็น array หากจำเป็น

            return (
                <div>
                    <label className={`block mb-2 text-base ${field.consentType !== "radio" ? "text-center" : ""}`}>
                        {field.label}{" "}
                        {field.required && field.consentType === "radio" && <span className="text-red-500">*</span>}
                    </label>
                    {field.consentType === "radio" && (
                        <div>
                            {options.map((option: any) => (
                                <div key={option.key} className="flex items-center mb-2 space-x-2">
                                    <input
                                        type="radio"
                                        id={option.key}
                                        name={field.key}
                                        value={option.key}
                                        className="form-radio"
                                    />
                                    <label
                                        htmlFor={`${option.key}`}
                                        className={`text-sm
                                        `}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );

        case "submit":
            return (
                <>
                    <div className="text-center">
                        <button
                            disabled={true}
                            className={`w-[50%] h-16 bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 text-xl
                        `}>
                            <span className="flex justify-center">{field.label}</span>
                        </button>
                    </div>
                </>
            );

        case "number":
            return (
                <>
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        <input
                            type="number"
                            id={field.key}
                            name={field.key}
                            className={`form-control w-full h-[40px] border p-2 rounded pl-2`}
                        />
                    </div>
                </>
            );
        default:
            return (
                <div>
                    <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                        type={field.type}
                        id={field.key}
                        name={field.key}
                        className={`form-control w-full h-[40px] border p-2 rounded pl-2`}
                    />
                </div>
            );
    }
};

export default DynamicFormServer;
